import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SubAva {
  id: string;
  name: string;
  is_active: boolean;
  org_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateSubAvaInput {
  name: string;
  is_active?: boolean;
}

export interface UpdateSubAvaInput {
  name?: string;
  is_active?: boolean;
}

/**
 * Hook to fetch all Sub-AVAs for the current organization
 * @param activeOnly - If true, only returns active Sub-AVAs (default: true)
 * @param searchTerm - Optional search term to filter by name
 */
export function useSubAvas(activeOnly: boolean = true, searchTerm?: string) {
  return useQuery({
    queryKey: ["sub-avas", activeOnly, searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("sub_ava")
        .select("*")
        .order("name", { ascending: true });

      if (activeOnly) {
        query = query.eq("is_active", true);
      }

      if (searchTerm) {
        query = query.ilike("name", `%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as SubAva[];
    },
  });
}

/**
 * Hook to fetch a single Sub-AVA by ID
 */
export function useSubAva(id: string | undefined) {
  return useQuery({
    queryKey: ["sub-ava", id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("sub_ava")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as SubAva;
    },
    enabled: !!id,
  });
}

/**
 * Hook to create a new Sub-AVA
 */
export function useCreateSubAva() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateSubAvaInput) => {
      // Get org_id from current user
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("sub_ava")
        .insert([{
          name: input.name,
          is_active: input.is_active ?? true,
          org_id: userData.org_id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as SubAva;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sub-avas"] });
      toast({
        title: "Sub-AVA created",
        description: "The Sub-AVA has been successfully created.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating Sub-AVA",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to update an existing Sub-AVA
 */
export function useUpdateSubAva() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateSubAvaInput }) => {
      const { data, error } = await supabase
        .from("sub_ava")
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as SubAva;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sub-avas"] });
      queryClient.invalidateQueries({ queryKey: ["sub-ava"] });
      toast({
        title: "Sub-AVA updated",
        description: "The Sub-AVA has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating Sub-AVA",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to delete a Sub-AVA (soft delete by deactivating)
 */
export function useDeleteSubAva() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Soft delete by setting is_active to false
      const { error } = await supabase
        .from("sub_ava")
        .update({ is_active: false })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sub-avas"] });
      toast({
        title: "Sub-AVA deactivated",
        description: "The Sub-AVA has been deactivated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deactivating Sub-AVA",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to get Sub-AVAs formatted for Select/Dropdown components
 */
export function useSubAvaOptions() {
  const { data: subAvas, isLoading, error } = useSubAvas(true);

  const options = subAvas?.map((ava) => ({
    value: ava.id,
    label: ava.name,
    name: ava.name,
  })) ?? [];

  return { options, isLoading, error };
}
